import type { RewardsResponse } from '@xchange/shared';
import { amountText, xgeText } from './assets.js';

/** What goes with the link when there is nothing more to say. No price, no promises. */
export const SHARE_TEXT =
  'Swap Gaju, USDC and XGE from your own wallet. Every swap earns XGE. This is my link:';

/**
 * The ready message for an invite: what xchange is, and what both people
 * get and when, from the API's own figures. "I swap on xchange from my own
 * wallet. Join with my link and we both get 50 XGE once you have swapped $10."
 */
export function inviteText(r: Pick<RewardsResponse, 'referralXge' | 'referralMinUsd'>): string {
  const xge = xgeText(r.referralXge).replace(/\.00$/, '');
  const min = BigInt(r.referralMinUsd);
  const when =
    min > 0n
      ? `once you have swapped ${amountText('USDC', min).replace(/\.00$/, '')}`
      : 'after your first swap';
  return `I swap on xchange from my own wallet. Join with my link and we both get ${xge} XGE ${when}.`;
}

/**
 * The line on the invite card: what both get, when, and what does not count.
 * "When a friend joins with your link and swaps $10, you both get 50 XGE.
 * Trades between the two of you do not count."
 */
export function inviteLine(
  r: Pick<RewardsResponse, 'referralXge' | 'referralMinUsd' | 'referred' | 'referredQualified'>,
): string {
  const xge = xgeText(r.referralXge).replace(/\.00$/, '');
  const min = BigInt(r.referralMinUsd);
  const when =
    min > 0n ? `swaps ${amountText('USDC', min).replace(/\.00$/, '')}` : 'makes a first swap';
  const brought =
    r.referred > 0 ? ` You have brought ${r.referred}, ${r.referredQualified} swapping.` : '';
  return `When a friend joins with your link and ${when}, you both get ${xge} XGE. Trades between the two of you do not count.${brought}`;
}

/**
 * The places a link is shared to, in the order shown: WhatsApp first, as
 * most people we reach use it. The message goes with the link everywhere
 * but Facebook, which takes a link alone.
 */
export function shareTargets(
  link: string,
  message: string = SHARE_TEXT,
): readonly (readonly [string, string])[] {
  const url = encodeURIComponent(link);
  const text = encodeURIComponent(message);
  return [
    ['WhatsApp', `https://wa.me/?text=${encodeURIComponent(`${message} ${link}`)}`],
    ['Telegram', `https://t.me/share/url?url=${url}&text=${text}`],
    ['X', `https://twitter.com/intent/tweet?text=${text}&url=${url}`],
    ['Facebook', `https://www.facebook.com/sharer/sharer.php?u=${url}`],
  ];
}

/**
 * "Bring a friend: 50 XGE each, once they have swapped $10. You have brought
 * 2, 1 swapping." The threshold comes from the API, so the words follow it.
 */
export function referralLine(
  r: Pick<RewardsResponse, 'referralXge' | 'referralMinUsd' | 'referred' | 'referredQualified'>,
): string {
  const min = BigInt(r.referralMinUsd);
  const when =
    min > 0n
      ? `once they have swapped ${amountText('USDC', min).replace(/\.00$/, '')}`
      : 'once they have made their first swap';
  const brought =
    r.referred > 0 ? ` You have brought ${r.referred}, ${r.referredQualified} swapping.` : '';
  return `Bring a friend: ${xgeText(r.referralXge)} XGE each, ${when}.${brought}`;
}
